import { supabaseClient } from 'supabase'

/**
 * Where a desk row's pictures live. A raw item's images are storage paths in
 * the `events` bucket, in stored order: `image_path` is image 1, then
 * `extra_image_paths` (deskPrefill.editorPickPath uses the same order).
 */
export type DeskImageSource = {
  image_path: string | null
  extra_image_paths: string[] | null
}

export type DeskShownImageSource = DeskImageSource & {
  desk_display_image_index: number | null
  display_image_index: number | null
}

/** Every stored image path, image 1 first; empty paths dropped. */
export const deskImagePaths = (row: DeskImageSource): string[] =>
  [row.image_path, ...(row.extra_image_paths ?? [])].filter(
    (path): path is string => typeof path === 'string' && path.trim() !== ''
  )

/**
 * The image the row currently shows (UI-SPEC §B): the desk's own swap, else
 * the editor's pick, else image 1. An index past the stored images falls back
 * to image 1 rather than showing nothing.
 */
export const shownImagePath = (row: DeskShownImageSource): string | null => {
  const paths = deskImagePaths(row)
  if (paths.length === 0) return null
  const index = row.desk_display_image_index ?? row.display_image_index ?? 1
  return paths[index - 1] ?? paths[0]
}

/** The public URL of a stored path in the `events` bucket. */
export const publicImageUrl = (path: string): string =>
  supabaseClient.storage.from('events').getPublicUrl(path).data.publicUrl
